import React, { useState, useEffect } from "react";
import { fetchMyEbayListings } from "../services/ebayService";
import pshService from "../services/pshService";
import { databaseLocation } from "../../../dummyData";
import normalizeMaker from "./utils";

const EbayListings = ({ credentials }) => {
  const [listings, setListings] = useState([]);
  const [filteredListings, setFilteredListings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [countryFilter, setCountryFilter] = useState("all");
  const [manufacturerFilter, setManufacturerFilter] = useState("all");
  const [availableCountries, setAvailableCountries] = useState([]);
  const [availableManufacturers, setAvailableManufacturers] = useState([]);
  const [loadingPrices, setLoadingPrices] = useState({});
  const [pshPrices, setPshPrices] = useState({});
  const [pageCount, setPageCount] = useState(1);
  const [startDateFilter, setStartDateFilter] = useState("all");
  const [startDateToFilter, setStartDateToFilter] = useState("all");
  const [minimumProfit, setMinimumProfit] = useState(20.0);
  const [quantities, setQuantities] = useState({});
  const [loadingQuantities, setLoadingQuantities] = useState({});

  const getManufacturerFromSku = (sku) => {
    if (!sku) return "Unknown";
    const manufacturer = sku.split("_")[1];
    if (!manufacturer) return "Other";

    // Normalize specific manufacturers to MD-Teile
    return normalizeMaker(manufacturer);
  };

  const checkAllQuantities = async () => {
    const listingsWithSku = filteredListings.filter((listing) => listing.sku);

    // Set all relevant listings to loading state
    setLoadingQuantities((prev) => {
      const newState = { ...prev };
      listingsWithSku.forEach((listing) => {
        newState[listing.id] = true;
      });
      return newState;
    });

    // Fetch quantities for all listings with SKUs
    for (const listing of listingsWithSku) {
      const articleNumber = listing.sku.split("_")[0];
      const quantity = await fetchQuantityForArticle(articleNumber);

      setQuantities((prev) => ({
        ...prev,
        [listing.id]: quantity,
      }));

      setLoadingQuantities((prev) => ({
        ...prev,
        [listing.id]: false,
      }));
    }
  };

  const fetchQuantityForArticle = async (articleNumber) => {
    try {
      const response = await fetch(
        `${databaseLocation}/product/search/${articleNumber}`
      );
      if (!response.ok) throw new Error("Failed to fetch quantity");
      const products = await response.json();
      return products.reduce(
        (total, product) => total + (product.quantity || 0),
        0
      );
    } catch (error) {
      console.error(
        `Error fetching quantity for ${articleNumber} from ${`${databaseLocation}/product/search/${articleNumber}`}:`,
        error
      );
      return 0;
    }
  };

  const fetchListings = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchMyEbayListings(
        credentials,
        startDateFilter,
        startDateToFilter,
        pageCount
      );

      console.log(data[0]);

      setListings(data);

      // Extract unique countries
      const countries = [...new Set(data.map((listing) => listing.country))];
      setAvailableCountries(countries);

      // Extract unique manufacturers from SKUs
      const manufacturers = [
        ...new Set(
          data
            .filter((listing) => listing.sku)
            .map((listing) => getManufacturerFromSku(listing.sku))
        ),
      ];
      setAvailableManufacturers(manufacturers.sort());

      setFilteredListings(data);

      // Initialize quantities state
      const emptyQuantities = data.reduce((acc, listing) => {
        acc[listing.id] = null;
        return acc;
      }, {});
      setQuantities(emptyQuantities);
    } catch (error) {
      console.error("Error fetching eBay listings:", error);
      setError("Failed to fetch eBay listings. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchListings();
  }, [credentials, pageCount]);

  useEffect(() => {
    let filtered = [...listings];

    console.log("Filtering from " + filtered.length + " listings ...");

    // Apply country filter
    if (countryFilter !== "all") {
      filtered = filtered.filter(
        (listing) => listing.country === countryFilter
      );
    }

    // Apply manufacturer filter
    if (manufacturerFilter !== "all") {
      if (manufacturerFilter === "Other") {
        filtered = filtered.filter(
          (listing) =>
            !listing.sku ||
            !availableManufacturers.includes(
              getManufacturerFromSku(listing.sku)
            )
        );
      } else {
        filtered = filtered.filter(
          (listing) =>
            listing.sku &&
            getManufacturerFromSku(listing.sku) === manufacturerFilter
        );
      }
    }

    // Apply start date filter (from)
    if (startDateFilter !== "all") {
      filtered = filtered.filter((listing) => {
        const startDate = new Date(startDateFilter);
        startDate.setHours(0, 0, 0, 0); // Set to start of the selected day
        return (
          new Date(listing.startTime) >= startDate &&
          new Date(listing.startTime) <= new Date(startDateToFilter)
        );
      });
    }

    setFilteredListings(filtered);
  }, [
    countryFilter,
    manufacturerFilter,
    startDateFilter,
    startDateToFilter,
    listings,
    availableManufacturers,
  ]);

  const formatEndTime = (endTimeStr) => {
    const endTime = new Date(endTimeStr);
    return endTime.toLocaleString();
  };

  const formatStartTime = (startTimeStr) => {
    const startTime = new Date(startTimeStr);
    return startTime.toLocaleString();
  };

  const formatPrice = (price, currency) => {
    if (!price || isNaN(price)) {
      // console.log("Invalid price value:", price);
      return `0.00 ${currency}`;
    }

    try {
      const numericPrice = parseFloat(price);
      return numericPrice.toFixed(2) + " " + currency;
    } catch (error) {
      console.error("Error formatting price:", error);
      return `${price} ${currency}`;
    }
  };

  const isPriceBelowMinimumProfit = (listing) => {
    if (!pshPrices[listing.id]?.found) return false;
    return listing.profit < minimumProfit;
  };

  const getPshPrice = async (listing) => {
    if (!listing.sku) {
      console.log("Listing has no sku!");
      return;
    }

    let articleNumber = listing.sku.split("_")[0];
    let manufacturer = getManufacturerFromSku(listing.sku);

    setLoadingPrices((prev) => ({
      ...prev,
      [listing.id]: true,
    }));

    try {
      const pshData = await pshService.fetchPSHInfos(
        articleNumber.replace("+", ""),
        manufacturer
      );
      setPshPrices((prev) => ({
        ...prev,
        [listing.id]: pshData,
      }));
    } catch (error) {
      console.error("Error fetching PSH price:", error);
      setPshPrices((prev) => ({
        ...prev,
        [listing.id]: { found: false, error: error.message },
      }));
    } finally {
      setLoadingPrices((prev) => ({
        ...prev,
        [listing.id]: false,
      }));
    }
  };

  const checkAllPshPrices = async () => {
    const listingsWithSku = filteredListings.filter(
      (listing) =>
        listing.sku &&
        (!pshPrices[listing.id] || !pshPrices[listing.id]?.found) &&
        !loadingPrices[listing.id]
    );

    if (listingsWithSku.length === 0) {
      return;
    }

    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    // Process in batches of 5 to avoid overwhelming the API
    const batchSize = 5;
    for (let i = 0; i < listingsWithSku.length; i += batchSize) {
      const batch = listingsWithSku.slice(i, i + batchSize);

      for (const listing of batch) {
        await getPshPrice(listing);
        await sleep(1000); // Delay between each call
      }

      // Optional: longer delay between batches
      if (i + batchSize < listingsWithSku.length) {
        await sleep(5000);
      }
    }
  };

  const calculateProfit = (listing) => {
    if (!listing || !listing.price || !pshPrices[listing.id]?.found) {
      // console.log("Can't calculate profit for listing " + listing.id);
      return null;
    }

    const ebayFee = listing.price * 0.15;
    const mwst = listing.price * 0.19;
    const shipping = 10.0;
    const pshPrice = pshPrices[listing.id].price;

    const profit = listing.price - ebayFee - mwst - shipping - pshPrice;
    listing.profit = profit;
    return profit;
  };

  return (
    <div className="container mx-auto p-4">
      <div className="flex flex-wrap gap-4 mb-4">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-sm font-medium text-gray-700">
            Country
          </label>
          <select
            value={countryFilter}
            onChange={(e) => setCountryFilter(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="all">All Countries</option>
            {availableCountries.map((country) => (
              <option key={country} value={country}>
                {country}
              </option>
            ))}
          </select>
        </div>

        <div className="flex-1 min-w-[200px]">
          <label className="block text-sm font-medium text-gray-700">
            Manufacturer
          </label>
          <select
            value={manufacturerFilter}
            onChange={(e) => setManufacturerFilter(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="all">All Manufacturers</option>
            {availableManufacturers.map((manufacturer) => (
              <option key={manufacturer} value={manufacturer}>
                {manufacturer}
              </option>
            ))}
            <option value="Other">Other</option>
          </select>
        </div>

        <div className="flex-1 min-w-[200px]">
          <label className="block text-sm font-medium text-gray-700">
            Start Date (From)
          </label>
          <input
            type="date"
            value={startDateFilter}
            onChange={(e) => setStartDateFilter(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div className="flex-1 min-w-[200px]">
          <label className="block text-sm font-medium text-gray-700">
            Start Date (To)
          </label>
          <input
            type="date"
            value={startDateToFilter}
            onChange={(e) => setStartDateToFilter(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div className="flex-1 min-w-[200px]">
          <label className="block text-sm font-medium text-gray-700">
            Pages to fetch
          </label>
          <select
            value={pageCount}
            onChange={(e) => setPageCount(Number(e.target.value))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value={1}>1 Page</option>
            <option value={2}>2 Pages</option>
            <option value={3}>3 Pages</option>
            <option value={4}>4 Pages</option>
            <option value={5}>5 Pages</option>
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="block text-sm font-medium text-gray-700">
            Minimum Profit
          </label>
          <input
            type="number"
            step="10.0"
            min="0"
            value={minimumProfit}
            onChange={(e) => setMinimumProfit(Number(e.target.value))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="block text-sm font-medium text-gray-700">
            &nbsp;
          </label>
          <button
            onClick={checkAllPshPrices}
            className="mt-1 w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Check All PSH Prices
          </button>
        </div>
      </div>

      {error && (
        <div
          className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4"
          role="alert"
        >
          <span className="block sm:inline">{error}</span>
        </div>
      )}

      <div className="text-sm text-gray-600 mb-4">
        Showing {filteredListings.length} of {listings.length} listings
      </div>

      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center">
          <button
            onClick={checkAllQuantities}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors mr-4"
          >
            Check All Quantities
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Item
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Price
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Status
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  PSH Price
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredListings.map((listing) => (
                <tr
                  key={listing.id}
                  className={`${
                    isPriceBelowMinimumProfit(listing) &&
                    pshPrices[listing.id]?.found
                      ? "bg-red-50"
                      : !pshPrices[listing.id]?.found &&
                        quantities[listing.id] == 0
                      ? "bg-red-400"
                      : pshPrices[listing.id]?.found &&
                        isPriceBelowMinimumProfit(listing) &&
                        quantities[listing.id] == 0
                      ? "bg-red-400"
                      : quantities[listing.id] === 0
                      ? "bg-yellow-50"
                      : "bg-white"
                  }`}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      {listing.imageUrl && (
                        <img
                          src={listing.imageUrl}
                          alt={listing.title}
                          className="h-10 w-10 object-contain mr-3"
                        />
                      )}
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {listing.title}
                        </div>
                        <div className="text-sm text-gray-500">
                          {listing.sku}
                        </div>
                        <div className="text-sm text-gray-500">
                          {listing.id}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td
                    className={`px-6 py-4 text-sm flex flex-row gap-2 ${
                      isPriceBelowMinimumProfit(listing)
                        ? "text-green-600 font-medium"
                        : "text-gray-500"
                    }`}
                  >
                    <span>{formatPrice(listing.price, listing.currency)}</span>
                    {pshPrices[listing.id] ? (
                      <span>
                        {formatPrice(
                          pshPrices[listing.id].price,
                          listing.currency
                        )}{" "}
                        PSH
                      </span>
                    ) : (
                      "PSH Price not available"
                    )}
                    <span className="text-red-600">
                      {formatPrice(listing.price * 0.19, listing.currency)} MwSt
                    </span>
                    <span className="text-red-600">
                      {formatPrice(listing.price * 0.15, listing.currency)} Ebay
                    </span>
                    <span className="text-red-600">
                      {formatPrice(10.0, listing.currency)} Shipping
                    </span>
                    {calculateProfit(listing) !== null && (
                      <span className="text-green-600 font-bold">
                        {formatPrice(listing.profit, listing.currency)} Profit
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {listing.status}
                    {quantities[listing.id] !== null && (
                      <span
                        className={`ml-2 ${
                          quantities[listing.id] > 0
                            ? "text-green-600"
                            : "text-red-600 font-medium"
                        }`}
                      >
                        ({quantities[listing.id]} in stock)
                      </span>
                    )}
                    {loadingQuantities[listing.id] && (
                      <span className="ml-2 text-gray-500">(Loading...)</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {listing.sold > 0 ? `${listing.sold} sold` : null}
                    {loadingPrices[listing.id] ? (
                      <span>Loading...</span>
                    ) : pshPrices[listing.id] ? (
                      pshPrices[listing.id].found ? (
                        <span className="text-green-600">
                          {pshPrices[listing.id].price}
                        </span>
                      ) : (
                        <span className="text-red-600">Not found</span>
                      )
                    ) : (
                      listing.sku && (
                        <span className="text-gray-400">Not checked</span>
                      )
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm font-medium space-x-2 flex">
                    <a
                      href={listing.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-900"
                    >
                      View on eBay
                    </a>
                    {listing.sku && (
                      <>
                        <button
                          onClick={() => getPshPrice(listing)}
                          className="text-blue-600 hover:text-blue-900 ml-2"
                        >
                          Check PSH Price
                        </button>
                        <button
                          onClick={async () => {
                            if (listing.sku) {
                              setLoadingQuantities((prev) => ({
                                ...prev,
                                [listing.id]: true,
                              }));
                              const articleNumber = listing.sku.split("_")[0];
                              const quantity = await fetchQuantityForArticle(
                                articleNumber
                              );
                              setQuantities((prev) => ({
                                ...prev,
                                [listing.id]: quantity,
                              }));
                              setLoadingQuantities((prev) => ({
                                ...prev,
                                [listing.id]: false,
                              }));
                            }
                          }}
                          className="text-blue-600 hover:text-blue-900 ml-2"
                          disabled={loadingQuantities[listing.id]}
                        >
                          Check Quantity
                        </button>
                      </>
                    )}
                    {listing.sku && (
                      <a
                        href={`https://psh.eu/en/search-results/?q=${
                          listing.sku.split("_")[0]
                        }`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-600 hover:text-green-900 ml-2"
                      >
                        Open on PSH
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default EbayListings;
